// Session store — bearer + renewal material + the signed-in person + the
// active business. Persisted in localStorage (guarded) so a reload keeps the
// session; the active business id rides on every request as X-Business-Id.
//
// Which strategy minted the session ('authentik' | 'local') is stored too, so
// refresh/unlock/logout go back to the same strategy after a reload.

import { readJson, readStore, writeStore } from '../storage';
import type { AuthStrategyName, LoginResult, SessionMembership, SessionUser } from './types';

const K = {
  token: 'erp.token',
  refresh: 'erp.refresh',
  expiresAt: 'erp.expires_at',
  user: 'erp.user',
  strategy: 'erp.strategy',
  business: 'erp.business_id',
  branch: 'erp.branch_id',
  memberships: 'erp.memberships',
  idleLock: 'erp.idle_lock_seconds',
} as const;

type Listener = () => void;
const listeners = new Set<Listener>();

let token: string | null = readStore(K.token);
let refreshToken: string | null = readStore(K.refresh);
let expiresAt = Number(readStore(K.expiresAt) ?? 0) || 0;
let user: SessionUser | null = readJson<SessionUser>(K.user);
let strategy: AuthStrategyName | null = (readStore(K.strategy) as AuthStrategyName | null) ?? null;
let businessId: string | null = readStore(K.business);
let branchId: string | null = readStore(K.branch);
let memberships: SessionMembership[] = readJson<SessionMembership[]>(K.memberships) ?? [];
let idleLockSeconds = Number(readStore(K.idleLock) ?? 0) || 0;

function emit() {
  listeners.forEach((l) => l());
}

/** Forget the active business and its branch (a branch belongs to one business). */
function dropBusiness() {
  businessId = null;
  branchId = null;
  writeStore(K.business, null);
  writeStore(K.branch, null);
}

export const sessionStore = {
  getToken: (): string | null => token,
  getRefreshToken: (): string | null => refreshToken,
  getExpiresAt: (): number => expiresAt,
  getUser: (): SessionUser | null => user,
  getStrategy: (): AuthStrategyName | null => strategy,
  isAuthenticated: (): boolean => token != null,
  getBusinessId: (): string | null => businessId,
  /** Active branch (erp.branches): sent to erp-core as X-Branch-Id; null = the server default (main). */
  getBranchId: (): string | null => branchId,
  /** Node: memberships from the last login/unlock response ([] otherwise). */
  getMemberships: (): SessionMembership[] => memberships,
  /** Node: the server's idle lock in seconds (0 = unknown → the client default). */
  getIdleLockSeconds: (): number => idleLockSeconds,

  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /**
   * Persist a session. `fresh` (a sign-in) replaces memberships + idle lock
   * with what the result carries; a renewal (refresh, unlock) keeps them when
   * the result does not report them.
   */
  set(result: LoginResult, by: AuthStrategyName, opts: { fresh?: boolean } = {}): void {
    // A fresh sign-in by a DIFFERENT person must not inherit the previous
    // person's active business (it rides on every request as X-Business-Id
    // and the server answers 403 not_a_member for a business they are not in).
    // Same person signing in again keeps their choice.
    if (opts.fresh && user && user.phone !== result.user.phone) dropBusiness();
    token = result.tokens.accessToken;
    refreshToken = result.tokens.refreshToken || null;
    expiresAt = result.tokens.expiresIn && result.tokens.expiresIn > 0 ? Date.now() + result.tokens.expiresIn * 1000 : 0;
    user = result.user;
    strategy = by;
    if (opts.fresh) {
      memberships = result.memberships ?? [];
      idleLockSeconds = result.idleLockSeconds && result.idleLockSeconds > 0 ? result.idleLockSeconds : 0;
      // Node sessions carry the roster: a stored business outside it is stale.
      if (businessId && memberships.length && !memberships.some((m) => m.business_id === businessId)) dropBusiness();
    } else {
      if (result.memberships) memberships = result.memberships;
      if (result.idleLockSeconds && result.idleLockSeconds > 0) idleLockSeconds = result.idleLockSeconds;
    }
    writeStore(K.token, token);
    writeStore(K.refresh, refreshToken);
    writeStore(K.expiresAt, expiresAt ? String(expiresAt) : null);
    writeStore(K.user, JSON.stringify(user));
    writeStore(K.strategy, strategy);
    writeStore(K.memberships, memberships.length ? JSON.stringify(memberships) : null);
    writeStore(K.idleLock, idleLockSeconds ? String(idleLockSeconds) : null);
    emit();
  },

  /** Switch the active business (top-bar chip). Sent as X-Business-Id. */
  setBusinessId(id: string | null): void {
    if (id === businessId) return;
    // A branch belongs to one business: switching business drops it (the
    // server then answers with that business's main branch).
    const hadBusiness = businessId != null;
    businessId = id;
    writeStore(K.business, id);
    if (hadBusiness && branchId != null) {
      branchId = null;
      writeStore(K.branch, null);
    }
    emit();
  },

  /** Switch the active branch (business chip switcher / a branch card). null = main. */
  setBranchId(id: string | null): void {
    if (id === branchId) return;
    branchId = id;
    writeStore(K.branch, id);
    emit();
  },

  /** Local clear. Strategy-side logout (IdP session) is auth/index.ts signOut(). */
  clear(): void {
    token = null;
    refreshToken = null;
    expiresAt = 0;
    user = null;
    strategy = null;
    memberships = [];
    idleLockSeconds = 0;
    // The active business is the previous person's choice: the next person on
    // this browser must start from the server's default, not from it.
    businessId = null;
    writeStore(K.business, null);
    branchId = null;
    writeStore(K.branch, null);
    writeStore(K.memberships, null);
    writeStore(K.idleLock, null);
    writeStore(K.token, null);
    writeStore(K.refresh, null);
    writeStore(K.expiresAt, null);
    writeStore(K.user, null);
    writeStore(K.strategy, null);
    emit();
  },
};
